import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import clsx from 'clsx';
import { Fragment, useState } from 'react';

import useLabels from '~hooks/useLabels';
import { useMutatePreferences } from '~services/api/hooks/newsfeed.hooks';
import { Button } from './Button';
import { InfoPopup } from './InfoPopup';
import { TopicSelector } from './TopicSelector';

const DIALOG_ID = 'customize-frontpage-dialog';

type CustomizeDialogProps = {
  onClose: () => void;
};

export default function CustomizeDialog({ onClose }: CustomizeDialogProps) {
  const {
    hlNewsfeedCustomizeDialog,
    msgNewsfeedCustomizeDialog,
    btnNewsfeedCustomizeDialogCancel,
    btnNewsfeedCustomizeDialogConfirm,
    btnNewsfeedCustomizeDialogClose,
    hlNewsfeedCustomizeConfirm,
    msgNewsfeedCustomizeConfirm,
    btnNewsfeedCustomizeDialogReset,
  } = useLabels([
    'hlNewsfeedCustomizeDialog',
    'msgNewsfeedCustomizeDialog',
    'btnNewsfeedCustomizeDialogCancel',
    'btnNewsfeedCustomizeDialogConfirm',
    'btnNewsfeedCustomizeDialogClose',
    'hlNewsfeedCustomizeConfirm',
    'msgNewsfeedCustomizeConfirm',
    'btnNewsfeedCustomizeDialogReset',
  ]);

  const { save, topicIds, updateTopicIds, hasTopicsSelected, resetTopics } = useMutatePreferences();
  const [open, setOpen] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  // derived state
  const canConfirm = topicIds.length >= 3;

  function handleClose() {
    setOpen(false);
    setTimeout(() => {
      onClose?.();
    }, 1000);
  }

  function handleReset() {
    void resetTopics();
    handleClose();
  }

  async function handleConfirm() {
    if (canConfirm) {
      setIsSaving(true);
      const saved = await save();
      setIsSaved(saved);
      setIsSaving(false);
    }
  }

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        onClose={() => {
          /* This prevents the dialog from closing when clicking outside */
        }}
        className={DIALOG_ID}
      >
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="min-h-screen px-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-80"
              entered="opacity-80"
              leave="ease-in duration-200"
              leaveFrom="opacity-80"
              leaveTo="opacity-0"
            >
              <div aria-hidden="true" className={clsx(`${DIALOG_ID}--overlay`, 'fixed inset-0 bg-black')} />
            </TransitionChild>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="inline-block h-screen align-middle" aria-hidden="true">
              &#8203;
            </span>
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300 delay-100"
              enterFrom="opacity-0 scale-95 translate-y-10"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95 translate-y-10"
              key={'dialog-prepare-publishing'}
            >
              <DialogPanel
                className={clsx(
                  `${DIALOG_ID}--panel`,
                  'inline-block w-full max-w-2xl max-h-screen aspect-ratio-1/1 my-4 bg-white overflow-hidden align-middle transition-all transform shadow-2xl rounded-xl relative',
                )}
              >
                <div className="absolute inset-0 flex w-full h-full min-h-full flex-col justify-between">
                  <Dialog.Title as="h3" className={clsx(`${DIALOG_ID}--title`, 'mt-3')}>
                    {hlNewsfeedCustomizeDialog}
                  </Dialog.Title>

                  <Dialog.Description className={clsx(`${DIALOG_ID}--description`, 'mt-0')}>
                    {msgNewsfeedCustomizeDialog}
                  </Dialog.Description>

                  <div className={clsx(`${DIALOG_ID}--content`, 'flex flex-grow relative')}>
                    <TopicSelector topicIds={topicIds} onChange={updateTopicIds} />
                  </div>

                  <div className={clsx(`${DIALOG_ID}--actions`, 'p-4 flex justify-center bg-gray relative')}>
                    {hasTopicsSelected ? (
                      <Button onClick={handleReset}>{btnNewsfeedCustomizeDialogReset}</Button>
                    ) : (
                      <Button onClick={handleClose} disabled={isSaving}>
                        {btnNewsfeedCustomizeDialogCancel}
                      </Button>
                    )}
                    <div className="mx-2" aria-hidden="true" />
                    <Button
                      onClick={handleConfirm}
                      className={canConfirm && 'c-button--dark'}
                      processing={isSaving}
                      disabled={!canConfirm}
                    >
                      {btnNewsfeedCustomizeDialogConfirm}
                    </Button>

                    <div className="absolute right-4 top-0 bottom-0 flex items-center">
                      <InfoPopup />
                    </div>
                  </div>
                </div>
                <Transition
                  show={isSaved}
                  enter="ease-out duration-100"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute inset-0 flex w-full h-full min-h-full flex-col justify-between bg-white z-30">
                    <Dialog.Title as="h3" className={clsx(`${DIALOG_ID}--title`, 'mt-3')}>
                      {hlNewsfeedCustomizeConfirm}
                    </Dialog.Title>

                    <Dialog.Description className={clsx(`${DIALOG_ID}--description`, 'mt-0')}>
                      {msgNewsfeedCustomizeConfirm}
                    </Dialog.Description>

                    <div className={clsx(`${DIALOG_ID}--content`, 'mt-4')} />

                    <div className={clsx(`${DIALOG_ID}--actions`, 'z-10 mt-4 p-4 flex justify-center bg-gray')}>
                      <Button className="c-button--dark" onClick={handleClose}>
                        {btnNewsfeedCustomizeDialogClose}
                      </Button>
                    </div>
                  </div>
                </Transition>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
